"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {TripUnitAmenity} from "@/entities/trip/api/types";

import {useTranslation} from "@/shared/i18n";
import {Collapsible} from "@/shared/ui/data-display/collapsible";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

export interface TripAmenitiesAccordionProps {
    amenities: Array<TripUnitAmenity>;
}

interface AmenityGroup {
    key: string;
    label: string;
    items: Array<TripUnitAmenity>;
}

function formatCategoryLabel(category: string): string {
    if (!/[-_]/.test(category)) return category;
    return category
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

function groupAmenities(amenities: Array<TripUnitAmenity>, otherLabel: string): Array<AmenityGroup> {
    const groups: Array<AmenityGroup> = [];
    const groupsByKey = new Map<string, AmenityGroup>();

    for (const amenity of amenities) {
        const key = amenity.category ?? "other";
        let group = groupsByKey.get(key);
        if (!group) {
            group = {key, label: amenity.category ? formatCategoryLabel(amenity.category) : otherLabel, items: []};
            groupsByKey.set(key, group);
            groups.push(group);
        }
        group.items.push(amenity);
    }

    return groups;
}

export function TripAmenitiesAccordion({amenities}: TripAmenitiesAccordionProps): JSX.Element {
    const {t} = useTranslation();
    const groups = groupAmenities(amenities, t("trip.otherAmenitiesCategory"));
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());

    const toggleGroup = (key: string): void => {
        setOpenGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div className="trip-amenities-accordion">
            <Typography as="p" size={12} weight={600} color="paragraph2" className="trip-amenities-accordion__title" options={["uppercase"]}>
                {t("trip.inTheHome")}
            </Typography>

            <ul className="trip-amenities-accordion__list">
                {groups.map((group) => {
                    const isOpen = openGroups.has(group.key);
                    return (
                        <li key={group.key}>
                            <Collapsible
                                label={group.label}
                                isOpen={isOpen}
                                onToggle={() => toggleGroup(group.key)}
                                icon={
                                    <SmartImage
                                        src={assetPath("/images/icons/chevron-down.svg")}
                                        alt=""
                                        width={16}
                                        height={16}
                                        className={`trip-amenities-accordion__chevron${isOpen ? " trip-amenities-accordion__chevron--open" : ""}`}
                                    />
                                }
                            >
                                <ul className="trip-amenities-accordion__group-list">
                                    {group.items.map((amenity, index) => (
                                        <li key={amenity.amenityId ?? `${group.key}-${index}`} className="trip-amenities-accordion__group-item">
                                            <Typography as="span" size={14}>
                                                {amenity.displayName}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Collapsible>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
